import { createTheme } from '@mui/material/styles';

// ─── FinMaple 2026 Design Tokens ────────────────────────────────────
// Blueprint: white-dominant, premium, minimal, data-driven
// Primary interactive: FinMaple Blue #1E3A5F
// Accent (<=5% surface): Maple Red #D32F2F
// Canonical tokens: src/theme/colorTokens.js
// No gradients. No green-dominant backgrounds.

const neutrals = {
  0: '#FFFFFF',
  25: '#F7F9FC',
  50: '#F7F9FC',
  100: '#E6E9EE',
  200: '#E6E9EE',
  400: '#5F6B7A',
  700: '#1A1A1A',
  900: '#1A1A1A',
};

const brand = {
  blue: '#1E3A5F',
  blueHover: '#1A3152',
  red: '#D32F2F',
};

const status = {
  success: '#2E7D32',
  warning: '#F9A825',
  error: '#C62828',
};

const elevation = {
  1: '0 6px 18px rgba(0,0,0,0.05)',    // cards default
  2: '0 14px 40px rgba(0,0,0,0.08)',   // hover
  3: '0 20px 60px rgba(0,0,0,0.12)',   // featured / recommended
};

// ─── Font Stacks ──────────────────────────────────────────────────
const HEADING_FONT = "'Satoshi', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const BODY_FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const theme = createTheme({
  palette: {
    primary: {
      main: brand.blue,
      dark: brand.blueHover,
      light: '#2E4F7A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brand.red,
      dark: '#B71C1C',
      light: '#E04852',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: status.warning,
      dark: '#7D5A00',
      contrastText: '#FFFFFF',
    },
    error: {
      main: status.error,
      dark: '#9B1B1B',
      contrastText: '#FFFFFF',
    },
    success: {
      main: status.success,
      dark: '#1B5E20',
      contrastText: '#FFFFFF',
    },
    info: {
      main: brand.blue,
      dark: '#152D4A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: neutrals[0],
      paper: neutrals[0],
    },
    text: {
      primary: neutrals[900],
      secondary: neutrals[700],
      disabled: neutrals[400],
    },
    divider: neutrals[200],
    neutral: neutrals,
    brand,
    status,
  },
  typography: {
    fontFamily: BODY_FONT,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    // Display - hero only (Satoshi) — responsive via CSS clamp
    h1: {
      fontFamily: HEADING_FONT,
      fontSize: 'clamp(2rem, 5vw + 0.5rem, 3rem)',     // 32px → 48px
      lineHeight: 1.15,
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    // H1 - page-level (Satoshi) — responsive
    h2: {
      fontFamily: HEADING_FONT,
      fontSize: 'clamp(1.75rem, 4vw + 0.5rem, 2.5rem)', // 28px → 40px
      lineHeight: 1.2,
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    // H2 - section (Satoshi) — responsive
    h3: {
      fontFamily: HEADING_FONT,
      fontSize: 'clamp(1.5rem, 3vw + 0.25rem, 2rem)',   // 24px → 32px
      lineHeight: 1.25,
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    // H3 - subsection (Satoshi) — responsive
    h4: {
      fontFamily: HEADING_FONT,
      fontSize: 'clamp(1.25rem, 2vw + 0.25rem, 1.5rem)', // 20px → 24px
      lineHeight: 1.35,
      fontWeight: 600,
    },
    // Body L (educational cards, limited use)
    h5: {
      fontSize: '1.125rem',   // 18px
      lineHeight: 1.6,
      fontWeight: 400,
    },
    // Body M - default (tabular nums for financial data)
    body1: {
      fontSize: '1rem',       // 16px
      lineHeight: 1.65,
      fontWeight: 400,
      fontFeatureSettings: "'tnum'",
    },
    // Body S - meta, filters (tabular nums for financial data)
    body2: {
      fontSize: '0.9375rem',  // 15px (was 14px)
      lineHeight: 1.55,
      fontWeight: 400,
      fontFeatureSettings: "'tnum'",
    },
    // Caption - disclosure microline, timestamps
    caption: {
      fontSize: '0.75rem',    // 12px
      lineHeight: '1rem',     // 16px
      fontWeight: 400,
    },
    // Label - buttons, badges
    subtitle1: {
      fontSize: '0.875rem',   // 14px (was 13px)
      lineHeight: 1.5,
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.875rem',   // 14px
      lineHeight: '1.25rem',  // 20px
      fontWeight: 500,
    },
    button: {
      fontSize: '0.9375rem',  // 15px (was 14px)
      fontWeight: 500,
      letterSpacing: '0.01em',
      textTransform: 'none',
    },
    overline: {
      fontSize: '0.75rem',    // 12px
      lineHeight: '1rem',     // 16px
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
    },
  },
  spacing: 4, // Base unit: 4px. Spacing scale: 4/8/12/16/24/32/48/64/96
  shape: {
    borderRadius: 12, // Default radius token (6/12/16 system)
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1248,  // Max content width per blueprint
      xl: 1440,
    },
  },
  components: {
    // Disable TouchRipple globally. MUI's ButtonBase defers the <span>
    // via useState(false)+useEffect, so the first render has no ripple
    // but Puppeteer captures post-effect HTML that includes it → hydration
    // mismatch. Disabling ripple makes both renders identical.
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: neutrals[0],
          color: neutrals[900],
        },
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: 'lg',
      },
      styleOverrides: {
        root: {
          paddingLeft: '16px',
          paddingRight: '16px',
          '@media (min-width:600px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
          '@media (min-width:1248px)': {
            paddingLeft: '24px',
            paddingRight: '24px',
          },
          '@media (min-width:1440px)': {
            paddingLeft: '80px',
            paddingRight: '80px',
          },
        },
        maxWidthLg: {
          '@media (min-width:1248px)': {
            maxWidth: '1248px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.9375rem',  // 15px (was 14px)
          minHeight: 40,
          transition: 'all 0.2s ease',
          '&:active': {
            transform: 'scale(0.97)',
          },
        },
        containedPrimary: {
          height: '44px',
          backgroundColor: brand.blue,
          color: '#FFFFFF',
          boxShadow: `0 2px 8px rgba(30,58,95,0.18)`,
          '&:hover': {
            backgroundColor: brand.blueHover,
            boxShadow: `0 4px 14px rgba(30,58,95,0.25)`,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'scale(0.97) translateY(0)',
          },
          '&:focus-visible': {
            outline: `2px solid ${brand.blue}`,
            outlineOffset: '2px',
          },
        },
        containedSizeLarge: {
          minHeight: 44,
          height: '48px',
          padding: '12px 24px',
        },
        outlined: {
          height: '44px',
          borderColor: neutrals[200],
          color: neutrals[900],
          '&:hover': {
            backgroundColor: neutrals[50],
            borderColor: neutrals[200],
          },
        },
        text: {
          color: neutrals[700],
          '&:hover': {
            textDecoration: 'underline',
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: neutrals[0],
          border: `1px solid ${neutrals[100]}`,
          borderRadius: '16px',
          boxShadow: elevation[1],
          padding: '24px',
          transition: 'transform 0.22s ease, box-shadow 0.22s ease',
          '&:hover': {
            boxShadow: elevation[2],
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: neutrals[0],
          color: neutrals[900],
          boxShadow: 'none',
          borderBottom: `1px solid ${neutrals[200]}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          minHeight: 36,           // 36px — meets 44px touch target with margin
          borderRadius: '999px',
          fontSize: '0.8125rem',  // 13px (was 12px)
          fontWeight: 500,
          paddingLeft: 10,
          paddingRight: 10,
          border: `1px solid ${neutrals[100]}`,
          backgroundColor: neutrals[50],
          color: neutrals[700],
        },
        label: {
          paddingLeft: 8,
          paddingRight: 8,
        },
        outlined: {
          backgroundColor: neutrals[50],
          color: neutrals[700],
          borderColor: neutrals[200],
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '12px !important',
          border: `1px solid ${neutrals[100]}`,
          marginBottom: '8px',
          '&:before': { display: 'none' },
          boxShadow: 'none',
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: elevation[1],
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            '& fieldset': {
              borderColor: neutrals[200],
            },
            '&:hover fieldset': {
              borderColor: neutrals[400],
            },
            '&.Mui-focused fieldset': {
              borderColor: brand.blue,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: neutrals[200],
          padding: '12px 16px',
          fontFeatureSettings: "'tnum'",
          '@media (max-width:599px)': {
            padding: '10px 12px',    // tighter on mobile to avoid overflow
            fontSize: '0.8125rem',   // 13px on mobile
          },
        },
        head: {
          fontWeight: 600,
          backgroundColor: neutrals[0],
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: '52px',
          '&:nth-of-type(even)': {
            backgroundColor: neutrals[25],
          },
        },
      },
    },
  },
  zIndex: {
    appBar: 1200,
  },
});

// ─── Reusable Typography Utilities ────────────────────────────────
// Spread into sx props: sx={{ ...tnum, fontSize: '2rem' }}
export const tnum = { fontFeatureSettings: "'tnum'" };

// Export tokens for direct use in components
export const tokens = { neutrals, brand, status, elevation };
export default theme;
